import { reportWorkspaceAccessLost, workspaceHeadersForXhr } from "./workspaceFetch";

export type UploadProgress = { loaded: number; total: number };

export type UploadResponse = {
  ok: boolean;
  status: number;
  json: () => Promise<any>;
};

/**
 * POSTs a form and reports upload progress. A fixed deadline fails large files
 * on slow phone connections that are still making progress, so this only gives
 * up when no bytes move for `idleMs`, or when the server hasn't answered
 * `responseMs` after the body finished sending.
 *
 * Failures match fetch: a dropped connection rejects with a TypeError and a
 * stall rejects with a "TimeoutError", so existing retry handling still applies.
 * The workspace header is added the same way the global fetch bridge does it.
 */
export function postFormWithProgress(
  url: string,
  body: FormData,
  {
    headers = {},
    onProgress,
    idleMs = 30000,
    responseMs = 90000,
  }: {
    headers?: Record<string, string>;
    onProgress?: (progress: UploadProgress) => void;
    idleMs?: number;
    responseMs?: number;
  } = {},
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      xhr.abort();
      reject(error);
    };
    const waitAtMost = (milliseconds: number) => {
      clearTimeout(timer);
      timer = setTimeout(
        () => fail(Object.assign(new Error("The upload took too long."), { name: "TimeoutError" })),
        milliseconds,
      );
    };

    xhr.open("POST", url);
    const requestHeaders = workspaceHeadersForXhr(url, headers);
    requestHeaders.forEach((value, name) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      waitAtMost(idleMs);
      onProgress?.({ loaded: event.loaded, total: event.lengthComputable ? event.total : 0 });
    };
    // The body is sent; the server may still be processing the image.
    xhr.upload.onload = () => waitAtMost(responseMs);
    xhr.onerror = () => fail(new TypeError("Failed to fetch"));
    xhr.onload = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (xhr.status === 403 && requestHeaders.has("authorization")) reportWorkspaceAccessLost();
      const text = xhr.responseText;
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: async () => JSON.parse(text),
      });
    };
    waitAtMost(idleMs);
    xhr.send(body);
  });
}
